// src/views/Checkout/Checkout.js
import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import CartBottomMenu from "../../components/CartBottomMenu/CartBottomMenu";
import { useNavigate } from "react-router-dom";
import "./Checkout.css";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";
import { localizeCartItems } from "../../utils/productLoader";
import { pickDisplayPrice, formatMoney } from "../../utils/pricePicker";
import LocationInputsForCheckout from "../../components/LocationInputsCheckout/LocationInputsCheckout";
import FormPhoneInput from "../../components/FormPhoneInput/FormPhoneInput";

import iziyco3dsecureImage from "../../assets/iziyco3d.webp";
import iziycoPayImage from "../../assets/iziycopay.webp";

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const setSeo = useSEO();
  const navigate = useNavigate();

  useEffect(() => {
    setSeo({
      title: t("checkout.meta.title"),
      description: t("checkout.meta.description"),
      keywords: t("checkout.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);

  // ---------------- STATE ----------------
  const [cartItems, setCartItems] = useState([]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("90");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");

  const [isCodeSent, setIsCodeSent] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [creditCardNumber, setCreditCardNumber] = useState("");
  const [creditCardName, setCreditCardName] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [securityCode, setSecurityCode] = useState("");

  const [isEInvoice, setIsEInvoice] = useState(false);

  // fiyat
  const { currency, conversionRate } = useCurrency();

  // taksit (BIN → direkt ödeme için)
  const [installments, setInstallments] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState(null);

  // 3DS
  const [use3ds, setUse3ds] = useState(false);
  const iyziWinRef = useRef(null); // açtığımız popup referansı
  const [orderCreating, setOrderCreating] = useState(false);

  // API uçları
  const HOSTED_INIT_URL =
    "https://www.apimukemel.com/api/iziyco/checkout-init.php";
  const DIRECT_PAY_URL = "https://www.apimukemel.com/api/direct-payment.php";
  const PAYPAL_URL = "https://www.apimukemel.com/api/paypal-payment.php";

  // ---------------- EFFECTS ----------------
  // BIN → taksit
  useEffect(() => {
    const digits = creditCardNumber.replace(/\D/g, "");
    if (digits.length >= 6) {
      const bin = digits.slice(0, 6);
      fetch("https://apimukemel.com/api/get-installments.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cardNumber: bin, price: 100.0 }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") setInstallments(data.installments);
        })
        .catch((err) => console.error("Taksit alınamadı", err));
    } else {
      setInstallments([]);
    }
  }, [creditCardNumber]);

  // boş sepet
  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("cart")) || [];
    if (items.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "There are no items in your cart",
        text: "Please select a product first!",
        confirmButtonText: "Okey",
      }).then((result) => {
        if (result.isConfirmed) setTimeout(() => navigate("/shop"), 1000);
      });
    }
  }, [navigate]);

  // ---------------- HELPERS ----------------
  const parseLegacyNumber = (val) => {
    const n = Number(
      String(val ?? 0)
        .replace(/\./g, "")
        .replace(",", ".")
    );
    return Number.isFinite(n) ? n : 0;
  };

  const unitInCurrency = (item, curr) => {
    const u = pickDisplayPrice(item, curr);
    if (u) return Number(u.amount) || 0;
    if (curr === "TRY") return parseLegacyNumber(item.price);
    return 0;
  };

  const computeTotalsTRY = () => {
    const subtotalTry = cartItems.reduce(
      (sum, it) => sum + unitInCurrency(it, "TRY") * (it.quantity || 1),
      0
    );
    const totalQuantity = cartItems.reduce(
      (a, it) => a + (it.quantity || 1),
      0
    );
    const discountPct = discountInfo
      ? Number(discountInfo.discount_value) || 0
      : 0;
    const discountTry = subtotalTry * (discountPct / 100);
    const insuranceTry = insuranceChecked ? insurancePrice * totalQuantity : 0;
    const totalTry = subtotalTry - discountTry + insuranceTry;
    return { subtotalTry, discountTry, insuranceTry, totalTry, totalQuantity };
  };

  // --------- finalizeOrder: 3DS sonrası sipariş oluştur ---------
  // 3DS bittiğinde çağrılır: siparişi DB'ye yazar, yönlendirir
  // Checkout.js içindeki finalizeOrder'u bununla DEĞİŞTİR
  const finalizeOrder = async () => {
    if (orderCreating) return;
    setOrderCreating(true);

    try {
      // 1) Ödeme başlatılırken kaydettiğin snapshot'ı çek
      const saved = (() => {
        try {
          return JSON.parse(localStorage.getItem("order_info") || "{}");
        } catch {
          return {};
        }
      })();

      // 2) Snapshot öncelikli değerler (fallback olarak current state)
      const userIdSnap =
        sessionStorage.getItem("user_id") || saved.userId || null;

      const nameSnap = saved.name ?? name;
      const emailSnap = saved.email ?? email;
      const phoneSnap = saved.phone ?? phone;
      const addr1Snap = saved.address1 ?? address1;
      const addr2Snap = saved.address2 ?? address2;
      const citySnap = saved.city ?? city;
      const stateSnap = saved.state ?? state;
      const countrySnap = saved.country ?? country;
      const couponCodeSnap = saved.coupon_code ?? (discountInfo?.code || null);
      const cartSnap =
        Array.isArray(saved.cartItems) && saved.cartItems.length
          ? saved.cartItems
          : cartItems;

      const currencySnap =
        (saved.currency || currency || "TRY").toUpperCase() === "TL"
          ? "TRY"
          : (saved.currency || currency || "TRY").toUpperCase();

      const couponPct = Number(
        saved.coupon_pct ?? discountInfo?.discount_value ?? 0
      );

      // 3) Tutarı garanti altına al (önce snapshot, yoksa yeniden hesapla)
      const tryFromSnap = Number(saved.totalPrice || 0) || 0;

      const unitInCurrLocal = (item, curr) => {
        // mevcut unitInCurrency helper'ını kullanmak daha iyi ama
        // snapshot item yapısı aynıysa bu çağrıyı kullan.
        return unitInCurrency(item, curr);
      };

      // görüntü toplamı (gerekirse)
      const subtotalCurr = cartSnap.reduce(
        (s, it) => s + unitInCurrLocal(it, currencySnap) * (it.quantity || 1),
        0
      );

      const subtotalTrySnap = cartSnap.reduce(
        (s, it) => s + unitInCurrency(it, "TRY") * (it.quantity || 1),
        0
      );

      const discountCurrSnap = subtotalCurr * (couponPct / 100);
      const discountTrySnap = subtotalTrySnap * (couponPct / 100);
      const discountPct = discountInfo
        ? Number(discountInfo.discount_value) || 0
        : 0;
      const totalCurrRecalc = subtotalCurr - subtotalCurr * (discountPct / 100);

      // TRY toplamı gerekirse yeniden
      const { totalTry: totalTryRecalc } = computeTotalsTRY();

      let amountToPay =
        currencySnap === "TRY"
          ? tryFromSnap || Number(totalTryRecalc || 0)
          : Number(totalCurrRecalc || 0);

      // NaN/0 koruması (asla 0 yazma)
      if (!Number.isFinite(amountToPay) || amountToPay <= 0) {
        amountToPay =
          Math.max(
            Number(tryFromSnap || 0),
            Number(totalTryRecalc || 0),
            Number(totalCurrRecalc || 0)
          ) || 0.01; // minimum güvenlik
      }
      amountToPay = Number(amountToPay.toFixed(2));

      // 4) Adres objesini hazırla (backend tek kez json_encode etmeli)
      const shippingAddressObj = {
        phone: String(phoneSnap || ""),
        address1: String(addr1Snap || ""),
        address2: String(addr2Snap || ""),
        city: String(citySnap || ""),
        state: String(stateSnap || ""),
        country: String(countrySnap || ""),
      };

      // 5) Order payload (SNAPSHOT ÖNCELİKLİ)
      const orderBody = {
        user_id: userIdSnap,
        name: nameSnap,
        email: emailSnap,
        total_price: amountToPay, // number
        currency: currencySnap, // 'TRY' / 'USD' ...
        status: "Pending",
        order_date: new Date().toISOString(),
        // ÖNEMLİ: obje olarak gönderiyoruz; backend tarafı BİR KEZ json_encode etmeli
        shipping_address: shippingAddressObj,
        order_items: cartSnap.map((item) => {
          const unitTry = unitInCurrency(item, "TRY");
          return {
            product_id: Number(item.id),
            product_name: item.name,
            quantity: Number(item.quantity || 1),
            price: Number((unitTry * (item.quantity || 1)).toFixed(3)),
            color:
              item.selectedVariation?.name &&
              item.selectedVariation.name !== "Default"
                ? item.selectedVariation.name
                : item.attributes?.color || null,
            image_url:
              item.selectedVariation?.images?.[0] ||
              item.default_images?.[0] ||
              "/no-image.jpg",
          };
        }),
        coupon_code: couponCodeSnap,
        coupon_discount: Number(
          (currencySnap === "TRY" ? discountTrySnap : discountCurrSnap).toFixed(
            3
          )
        ),
      };

      // Debug lazım olursa:
      // console.log("ORDER_BODY (3DS finalize):", orderBody);

      const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderBody),
      });

      const raw = await orderRes.text();
      let orderResult;
      try {
        orderResult = JSON.parse(raw);
      } catch {
        // backend JSON döndürmüyorsa bile devam edebil
        orderResult = { ok: orderRes.ok, message: raw };
      }

      const orderId =
        orderResult?.id ||
        orderResult?.order_id ||
        orderResult?.data?.id ||
        null;

      if (
        orderRes.ok &&
        (orderResult?.message?.toLowerCase()?.includes("created") || orderId)
      ) {
        await Swal.fire({
          icon: "success",
          title: "Successfully",
          text: "Your order has been completed successfully.",
          showConfirmButton: true,
          confirmButtonText: "Okey",
        });
        navigate(orderId ? `/order/${orderId}` : "/");

        // temizlik
        setName("");
        setEmail("");
        setPhone("");
        setAddress1("");
        setAddress2("");
        setCity("");
        setState("");
        setCountry("");
        setIsEmailVerified(false);
        setVerificationCode("");
        setIsCodeSent(false);
      } else {
        Swal.fire(
          "Error",
          orderResult?.message || "The order could not be created.",
          "error"
        );
      }
    } catch (err) {
      console.error("Order finalize error:", err);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setOrderCreating(false);
      try {
        if (iyziWinRef.current && !iyziWinRef.current.closed) {
          iyziWinRef.current.close();
        }
      } catch {}
    }
  };

  // 3DS callback mesajını dinle
  useEffect(() => {
    const TRUSTED = [
      "https://www.apimukemel.com",
      "https://apimukemel.com",
      "http://localhost:3000",
    ];
    const onMsg = (e) => {
      if (!e?.data || !e.data.type) return;

      // local geliştirmede origin kontrolünü yumuşatıyoruz
      const safe =
        TRUSTED.includes(e.origin) ||
        (typeof window !== "undefined" &&
          window.location.hostname === "localhost");

      if (!safe) return;

      if (e.data.type === "PAY_OK") {
        finalizeOrder();
        try {
          if (iyziWinRef.current && !iyziWinRef.current.closed) {
            iyziWinRef.current.close();
          }
        } catch {}
      } else if (e.data.type === "PAY_FAIL") {
        Swal.fire(
          "Ödeme Başarısız",
          e.data.msg || "Please try again.",
          "error"
        );
        try {
          if (iyziWinRef.current && !iyziWinRef.current.closed) {
            iyziWinRef.current.close();
          }
        } catch {}
      }
    };

    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- ORDER SUBMIT (3DS + direkt + PayPal) ----------------
  const handleSubmitOrder = async (e, paymentMethod = "iyzico") => {
    e.preventDefault();

    if (userId && !isEmailVerified) {
      Swal.fire(
        "Verification Required",
        "Please verify your email address!",
        "warning"
      );
      return;
    }

    const cartItemsLS = JSON.parse(localStorage.getItem("cart")) || [];
    if (cartItemsLS.length === 0) {
      Swal.fire("Hata", "Sepetiniz boş.", "error");
      return;
    }

    try {
      // Kullanıcı kontrolü
      const existingUserResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/users?email=${encodeURIComponent(
          email
        )}`
      );
      const existingUserData = await existingUserResponse.json();

      let userId;
      if (existingUserData?.id) {
        userId = existingUserData.id;
      } else {
        const randomPassword = Math.random().toString(36).slice(-8);
        const newUser = {
          name,
          email,
          phone,
          password: randomPassword,
          address1,
          address2,
          city,
          state,
          country,
        };
        const createUserRes = await fetch(
          `${process.env.REACT_APP_API_URL}/users`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newUser),
          }
        );
        const createdUser = await createUserRes.json();
        if (!createdUser.id) {
          Swal.fire("Hata", createdUser.error || "Kayıt başarısız.", "error");
          return;
        }
        userId = createdUser.id;
      }

      // TRY toplam ve görüntü toplamı
      const { totalTry, discountTry, totalQuantity } = computeTotalsTRY();

      const getLineTotalCurr = (it) =>
        unitInCurrency(it, currency) * (it.quantity || 1);
      const subtotalCurr = cartItems.reduce(
        (s, it) => s + getLineTotalCurr(it),
        0
      );
      const discountPct = discountInfo
        ? Number(discountInfo.discount_value) || 0
        : 0;
      const discountCurr = subtotalCurr * (discountPct / 100);
      const insuranceUnitCurr = (() => {
        let key = String(currency || "TRY").toUpperCase();
        if (key === "TL") key = "TRY";
        if (insuranceMap && insuranceMap[key] != null)
          return Number(insuranceMap[key]) || 0;
        if (key === "USD") return Number(insuranceUSD) || 0;
        if (key === "TRY") return Number(insurancePrice) || 0;
        return Number(insuranceUSD) || 0;
      })();
      const insuranceCurr = insuranceChecked
        ? insuranceUnitCurr * totalQuantity
        : 0;
      const totalCurr = subtotalCurr - discountCurr + insuranceCurr;

      const couponCodeApplied = discountInfo?.code || null;
      const couponPct = Number(discountInfo?.discount_value || 0);

      // local info
      localStorage.setItem("conversionRate", conversionRate.toString());
      localStorage.setItem(
        "order_info",
        JSON.stringify({
          userId,
          name,
          email,
          phone,
          address1,
          address2,
          city,
          state,
          country,
          totalPrice: totalTry, // TRY
          currency,
          cartItems,
          coupon_code: couponCodeApplied,
          coupon_pct: couponPct,
        })
      );

      // ------------- Fatura kaydı -------------
      if (selectedTypes.individual || selectedTypes.corporate) {
        const invoiceData = selectedTypes.individual
          ? {
              type: "individual",
              full_name: individualData.full_name,
              email: individualData.email,
              phone: individualData.phone,
              personal_tax_number: individualData.personal_tax_number,
              address: individualData.address,
            }
          : {
              type: "corporate",
              email: corporateData.email,
              phone: corporateData.phone,
              company_name: corporateData.company_name,
              tax_office: corporateData.tax_office,
              corporate_tax_number: corporateData.corporate_tax_number,
              mersis_no: corporateData.mersis_no,
              address: corporateData.address,
              is_einvoice_taxpayer: corporateData.is_einvoice_taxpayer,
            };

        const payload = {
          user_id: userId,
          ...invoiceData,
          is_einvoice_taxpayer: isEInvoice ? 1 : 0,
        };

        try {
          const invoiceRes = await fetch(
            `${process.env.REACT_APP_API_URL}/invoice`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          const invoiceResult = await invoiceRes.json();
          if (invoiceResult.success === false) {
            Swal.fire(
              "Fatura Hatası",
              invoiceResult.message || "Fatura kaydı başarısız.",
              "error"
            );
            return;
          } else if (invoiceResult.success === true) {
            Swal.fire(
              "Fatura Başarılı",
              invoiceResult.message || "Fatura kaydı başarıyla oluşturuldu.",
              "success"
            );
          }
        } catch (error) {
          console.error("Fatura gönderim hatası:", error);
          Swal.fire(
            "Fatura Hatası",
            "Fatura bilgisi gönderilirken hata oluştu.",
            "error"
          );
          return;
        }
      }

      // ---------------- ÖDEME ----------------
      const payCurrency =
        String(currency || "TRY").toUpperCase() === "TL"
          ? "TRY"
          : String(currency || "TRY").toUpperCase();

      if (paymentMethod === "iyzico3ds") {
        // 3DS hosted: ad/soyad ayır + telefon normalize et
        const splitFullName = (full) => {
          const parts = String(full || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          if (parts.length === 0) return { first: "-", last: "-" };
          if (parts.length === 1) return { first: parts[0], last: "-" };
          const last = parts.pop();
          return { first: parts.join(" "), last };
        };
        const { first, last } = splitFullName(name);

        let phoneDigits = String(phone || "").replace(/\D/g, "");
        if (phoneDigits.startsWith("0")) phoneDigits = phoneDigits.slice(1);
        if (
          !phoneDigits.startsWith("90") &&
          (country || "").toUpperCase().includes("TÜR")
        ) {
          phoneDigits = "90" + phoneDigits;
        }

        const amountToPay =
          payCurrency === "TRY"
            ? Number(totalTry).toFixed(2)
            : Number(totalCurr).toFixed(2);

        const hostedPayload = {
          price: amountToPay,
          currency: payCurrency,
          name: first,
          surname: last,
          email,
          phone: phoneDigits,
          address: `${address1} ${address2}`.trim(),
          city,
          country,
          installment: payCurrency === "TRY" ? selectedInstallment ?? 1 : 1,
        };

        try {
          const r = await fetch(HOSTED_INIT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(hostedPayload),
          });
          const text = await r.text();
          let resp;
          try {
            resp = JSON.parse(text);
          } catch {
            Swal.fire("Ödeme Başlatılamadı", "Beklenen JSON gelmedi.", "error");
            return;
          }

          if (resp.status === "redirect" && resp.html) {
            // popup aç
            const win = window.open("", "iyzico", "width=480,height=720");
            iyziWinRef.current = win;

            const writeHtml = (htmlStr) => {
              win.document.open();
              win.document.write(htmlStr);
              win.document.close();
            };

            try {
              const b64ish =
                /^[A-Za-z0-9+/=\r\n]+$/.test(resp.html) &&
                resp.html.replace(/\s/g, "").length % 4 === 0;
              if (b64ish) {
                const bin = atob(resp.html.replace(/\s/g, ""));
                const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
                const htmlUtf8 = new TextDecoder("utf-8").decode(bytes);
                writeHtml(htmlUtf8);
              } else {
                writeHtml(resp.html);
              }
            } catch {
              writeHtml(resp.html);
            }
            return; // 3DS tamamlanınca callback -> postMessage -> finalizeOrder
          } else {
            Swal.fire(
              "Ödeme Başlatılamadı",
              resp.message || "Please try again.",
              "error"
            );
            return;
          }
        } catch (err) {
          Swal.fire("Bağlantı Hatası", err.message || "Network error", "error");
          return;
        }
      }

      if (paymentMethod === "iyzico") {
        // Kartla direkt ödeme
        const amountToPay =
          payCurrency === "TRY"
            ? Number(totalTry).toFixed(2)
            : Number(totalCurr).toFixed(2);

        const cardNum = creditCardNumber.replace(/\D/g, "");
        const [mmRaw, yyRaw] = (expirationDate || "").split("/");
        const expireMonth = String(mmRaw || "").padStart(2, "0");
        const expireYear =
          String(yyRaw || "").length === 2 ? "20" + yyRaw : String(yyRaw || "");
        const cvc = securityCode.replace(/\D/g, "").slice(0, 4);

        const paymentRes = await fetch(DIRECT_PAY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cardHolderName: (creditCardName || "").trim(),
            cardNumber: cardNum,
            expireMonth,
            expireYear,
            cvc,
            price: amountToPay,
            currency: payCurrency,
            email,
            name,
            phone,
            address: `${address1} ${address2}`,
            city,
            country,
            installment: payCurrency === "TRY" ? selectedInstallment ?? 1 : 1,
          }),
        });
        const paymentResult = await paymentRes.json();
        if (!paymentResult.status || paymentResult.status !== "success") {
          const extra = +paymentResult.error_code
            ? ` (Kod: ${paymentResult.error_code})`
            : "";
          Swal.fire(
            "Ödeme Başarısız",
            (paymentResult.message || "Lütfen tekrar deneyin.") + extra,
            "error"
          );
          return;
        }
        // direkt ödeme başarılı → aşağıda sipariş oluşturacağız
      } else if (paymentMethod === "paypal") {
        const paypalAmount = Number(totalCurr).toFixed(2);
        localStorage.setItem(
          "paypal_payment_info",
          JSON.stringify({ price: paypalAmount, currency })
        );

        const paymentRes = await fetch(PAYPAL_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            price: paypalAmount,
            currency,
            email,
            name,
            phone,
            address: `${address1} ${address2}`,
            city,
            country,
          }),
        });
        const paymentResult = await paymentRes.json();
        if (!paymentResult.status || paymentResult.status !== "success") {
          Swal.fire(
            "PayPal Ödeme Başarısız",
            paymentResult.message || "Lütfen tekrar deneyin.",
            "error"
          );
          return;
        }
        if (paymentResult.approveUrl) {
          window.location.href = paymentResult.approveUrl;
          return;
        }
      }

      // 3DS değilse (veya PayPal değilse) bu noktada ödeme tamam → siparişi oluştur
      const amountToPay =
        payCurrency === "TRY"
          ? Number(totalTry).toFixed(2)
          : Number(totalCurr).toFixed(2);

      // Kupon indirim tutarı (sipariş para biriminde)
      const couponDiscount =
        payCurrency === "TRY"
          ? Number((discountTry || 0).toFixed(2))
          : Number((discountCurr || 0).toFixed(2));

      const orderBody = {
        user_id: userId,
        name,
        email,
        total_price: Number(amountToPay),
        currency,
        status: "Pending",
        order_date: new Date().toISOString(),
        shipping_address: { phone, address1, address2, city, state, country },
        order_items: cartItems.map((item) => {
          const unitTry = unitInCurrency(item, "TRY");
          return {
            product_id: Number(item.id),
            product_name: item.name,
            quantity: Number(item.quantity),
            price: Number((unitTry * (item.quantity || 1)).toFixed(3)),
            color:
              item.selectedVariation?.name &&
              item.selectedVariation.name !== "Default"
                ? item.selectedVariation.name
                : item.attributes?.color || null,
            image_url:
              item.selectedVariation?.images?.[0] ||
              item.default_images?.[0] ||
              "/no-image.jpg",
          };
        }),
        coupon_code: discountInfo?.code || null,
        coupon_discount: couponDiscount, // <— YENİ
      };

      const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderBody),
      });
      const orderResult = await orderRes.json();
      const orderId = orderResult.id;

      if (orderResult.message === "Order created successfully") {
        Swal.fire({
          icon: "success",
          title: "Successfully",
          text: "Your order has been completed successfully.",
          showConfirmButton: true,
          confirmButtonText: "Okey",
        }).then(() => {
          navigate(`/order/${orderId}`);
          setName("");
          setEmail("");
          setPhone("");
          setAddress1("");
          setAddress2("");
          setCity("");
          setState("");
          setCountry("");
          setIsEmailVerified(false);
          setVerificationCode("");
          setIsCodeSent(false);
        });
      } else {
        Swal.fire("Error", "The order could not be created.", "error");
      }
    } catch (error) {
      console.error("Order process:", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    }
  };

  // ---------------- EMAIL VERIFY ----------------
  const userId = sessionStorage.getItem("user_id");
  const [, setUser] = useState(null);

  const handleSendCode = async () => {
    try {
      const isGuest = !userId;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/sendCode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, is_guest: isGuest }),
      });
      const result = await res.json();

      if (result.success) {
        setIsCodeSent(true);
        setEmailError("");
        Swal.fire({
          icon: "info",
          title: "Verification code sent",
          text: "Please enter the verification code from your email address.",
          confirmButtonText: "Okey",
        });
      } else {
        if (result.message === "Your email is already verified!") {
          setIsEmailVerified(true);
          setIsCodeSent(false);
          Swal.fire({
            icon: "success",
            title: "Already Verified",
            text: "This email is already verified.",
          });
        } else {
          setEmailError(result.message || "Code could not be sent.");
        }
      }
    } catch (error) {
      setEmailError("An error occurred.");
    }
  };

  const handleVerifyCode = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/sendCode`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const result = await res.json();
      if (result.success) {
        setIsEmailVerified(true);
        Swal.fire({
          icon: "success",
          title: "Email Verified veya Email Confirmed",
          text: "Your email address has been successfully verified.",
          confirmButtonText: "Okey",
        });
      } else {
        setEmailError("Code could not be verified.");
      }
    } catch (error) {
      setEmailError("An error occurred during verification.");
    }
  };

  // kullanıcı bilgisi
  useEffect(() => {
    if (userId) {
      const fetchUserData = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/users?id=${userId}`
          );
          if (!response.ok)
            throw new Error("User information could not be retrieved!");
          const data = await response.json();
          setUser(data);
        } catch (error) {
          console.error("API Error:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "An error occurred while retrieving user information!",
          });
        }
      };
      fetchUserData();
    }
  }, [userId]);

  // adresler
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [, setSelectedAddressId] = useState("");

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user_addresses?user_id=${userId}`
        );
        if (!response.ok)
          throw new Error("Address data could not be retrieved!");
        const data = await response.json();

        if (!Array.isArray(data) || data.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "Delivery address required",
            text: "Please enter your delivery address first.",
            confirmButtonText: "Okey",
          }).then((result) => {
            if (result.isConfirmed) {
              setTimeout(() => {
                window.location.href = `/profile/${userId}/useraddressinfo`;
              }, 2000);
            }
          });
          setAddresses([]);
          return;
        }
        setAddresses(data);
      } catch (error) {
        console.error("API Error:", error);
      }
    };
    if (userId) fetchAddresses();
  }, [userId]);

  const handleAddressChange = (e) => {
    const id = parseInt(e.target.value);
    const found = addresses.find((a) => a.id === id);
    setSelectedAddressId(id);
    setSelectedAddress(found);

    const selectedId = e.target.value;
    const selectedAddr = addresses.find(
      (address) => address.id === parseInt(selectedId)
    );
    setSelectedAddress(selectedAddr);

    if (found) {
      const formattedPhone = (found.phone || "").replace(/[^\d]/g, "");
      setPhone(formattedPhone || "");
      setAddress1(found.address1 || "");
      setAddress2(found.address2 || "");
      setCity(found.city || "");
      setState(found.state || "");
      setCountry(found.country || "");
    }
  };

  const getCartItemImage = (item) =>
    item.selectedVariation?.images?.[0] ||
    item.default_images?.[0] ||
    "/no-image.jpg";

  // kupon
  const [couponCode, setCouponCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const getTotalPrice = () => {
    const subtotal = cartItems.reduce(
      (total, item) =>
        total + unitInCurrency(item, "TRY") * (item.quantity || 1),
      0
    );
    let totalPrice = subtotal;
    if (discountInfo) {
      const pct = Number(discountInfo.discount_value) || 0;
      totalPrice = subtotal - subtotal * (pct / 100);
    }
    if (insuranceChecked) {
      const totalQty = cartItems.reduce(
        (acc, item) => acc + (item.quantity || 1),
        0
      );
      totalPrice += insurancePrice * totalQty;
    }
    return totalPrice.toFixed(3);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      await Swal.fire({
        icon: "warning",
        title: t("coupon.enterCodeTitle"),
        confirmButtonText: t("common.ok"),
      });
      return;
    }

    // Parfüm kontrolü (engelleme yok)
    const isPerfume = (item) => {
      const c = item?.category;
      if (Array.isArray(c))
        return c.some((cat) => String(cat).toLowerCase() === "perfume");
      if (typeof c === "string") return c.toLowerCase() === "perfume";
      return false;
    };
    const hasPerfumeCategory = cartItems.some(isPerfume); // sadece flag; şimdilik uyarma

    setIsApplyingCoupon(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/coupon?action=apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: couponCode, total: getTotalPrice() }),
        }
      );
      const data = await response.json();

      if (data.success) {
        // --- parfüm-dışı etkin yüzde hesabı (senin mevcut mantığın) ---
        const subtotalTry = cartItems.reduce(
          (sum, it) => sum + unitInCurrency(it, "TRY") * (it.quantity || 1),
          0
        );
        const eligibleTry = cartItems.reduce(
          (sum, it) =>
            sum +
            (isPerfume(it)
              ? 0
              : unitInCurrency(it, "TRY") * (it.quantity || 1)),
          0
        );
        const originalPct = parseFloat(data.discount_value) || 0;
        const ratio = subtotalTry > 0 ? eligibleTry / subtotalTry : 0;
        const effectivePct = Number((originalPct * ratio).toFixed(6));
        const newTotalTry = subtotalTry - eligibleTry * (originalPct / 100);

        setDiscountInfo({
          code: couponCode,
          discount: data.discount,
          discount_value: effectivePct, // ekranlar bunu kullanmaya devam ediyor
          new_total: Number(newTotalTry.toFixed(3)),
        });

        // 1) önce başarı
        await Swal.fire({
          icon: "success",
          title: t("coupon.appliedTitle"),
          confirmButtonText: t("common.ok"),
        });

        if (hasPerfumeCategory) {
          await Swal.fire({
            icon: "info",
            title: t("common.infoTitle"),
            text: t("coupon.perfumeInfoText"),
            confirmButtonText: t("common.ok"),
          });
        }
      } else {
        setDiscountInfo(null);
        await Swal.fire({
          icon: "error",
          title: t("coupon.invalidTitle"),
          confirmButtonText: t("common.ok"),
        });
      }
    } catch (error) {
      console.error("Coupon error:", error);
      await Swal.fire({
        icon: "error",
        title: t("common.errorOccurredTitle"),
        confirmButtonText: t("common.ok"),
      });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // ---------- INSURANCE ----------
  const [insurancePrice, setInsurancePrice] = useState(0); // TRY
  const [insuranceChecked, setInsuranceChecked] = useState(false);
  const [insuranceUSD, setInsuranceUSD] = useState(0);
  const [insuranceMap, setInsuranceMap] = useState({});

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/insurance`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "success") {
          setInsuranceUSD(Number(data.price_usd) || 0);
          setInsuranceMap(data.converted || {});
          setInsurancePrice(Number(data?.converted?.TRY) || 0);
        }
      })
      .catch((err) => console.error("Sigorta fiyatı alınamadı", err));
  }, []);

  const totalQuantity = cartItems.reduce(
    (acc, item) => acc + (item.quantity || 1),
    0
  );

  const insuranceUnitCurr = React.useMemo(() => {
    let key = String(currency || "TRY").toUpperCase();
    if (key === "TL") key = "TRY";
    if (insuranceMap && insuranceMap[key] != null) {
      return Number(insuranceMap[key]) || 0;
    }
    if (key === "USD") return Number(insuranceUSD) || 0;
    if (key === "TRY") return Number(insurancePrice) || 0;
    return Number(insuranceUSD) || 0;
  }, [insuranceMap, insuranceUSD, insurancePrice, currency]);

  const insuranceCurr = insuranceChecked
    ? insuranceUnitCurr * totalQuantity
    : 0;
  // ---------- /INSURANCE ----------

  // ---------------- Görüntü toplamları ----------------
  const getLineTotalCurr = (it) =>
    unitInCurrency(it, currency) * (it.quantity || 1);

  const subtotalCurr = cartItems.reduce((s, it) => s + getLineTotalCurr(it), 0);
  const discountPct = discountInfo
    ? Number(discountInfo.discount_value) || 0
    : 0;
  const discountCurr = subtotalCurr * (discountPct / 100);
  const totalCurr = subtotalCurr - discountCurr + insuranceCurr;

  // Select invoice type
  const [selectedTypes, setSelectedTypes] = useState({
    individual: false,
    corporate: false,
  });

  const [individualData, setIndividualData] = useState({
    phone: "90",
    email: "",
    address: "",
    full_name: "",
    personal_tax_number: "",
  });

  const [corporateData, setCorporateData] = useState({
    company_name: "",
    tax_office: "",
    corporate_tax_number: "",
    mersis_no: "",
    phone: "90",
    email: "",
    address: "",
  });

  const handleCheckboxChange = (e) => {
    const { id, checked } = e.target;
    setSelectedTypes((prev) => ({ ...prev, [id]: checked }));
  };

  const handleIndividualChange = (e) => {
    const { name, value } = e.target;
    setIndividualData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCorporateChange = (e) => {
    const { name, value } = e.target;
    setCorporateData((prev) => ({ ...prev, [name]: value }));
  };

  const getLocalizedColorName = (item, t) => {
    const selected = item.selectedVariation?.name;
    const defaultColor = item.color?.split(",")[0]?.trim();
    const rawColor =
      selected && selected !== "Default" ? selected.trim() : defaultColor || "";
    if (!rawColor) return "";
    const normalized =
      rawColor.charAt(0).toUpperCase() + rawColor.slice(1).toLowerCase();
    return t(normalized);
  };

  // dil değişince sepeti yerelleştir
  useEffect(() => {
    const handleLanguageChange = () => window.location.reload();
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  useEffect(() => {
    const fetchLocalizedCart = async () => {
      const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
      const localizedCart = await localizeCartItems(storedCart, i18n.language);
      setCartItems(localizedCart);
    };
    fetchLocalizedCart();
  }, [i18n.language]);

  // -------------- BURADAN SONRASI PART 2 (JSX) --------------
  return (
    <>
      <section className="bg-light">
        <div className="container-fluid">
          <div className="row py-5 justify-content-center align-items-start">
            {/* SOL SÜTUN */}
            <div className="col-lg-6 bg-white">
              <div className="p-3">
                <div className=" d-flex justify-content-between">
                  <h5 className="mt-2">
                    <i className="fa-solid fa-1 me-2 text-dark wbbgcolor text-white p-2"></i>
                    {t("DeliveryOptions")}
                  </h5>
                </div>

                {/* <form className="mt-3" onSubmit={handleSubmitOrder}> */}
                <form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                  <div className="row">
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control fw-light p-3"
                        placeholder={t("NameSurname")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    {/* Email + Doğrulama */}
                    {/* Email + Doğrulama */}
                    <div className="col-lg-12">
                      {userId ? (
                        // LOGIN KULLANICI: doğrulama akışı aktif
                        <div className="mb-3">
                          <div className="d-flex">
                            <input
                              type="email"
                              className="form-control fw-light p-3 me-2"
                              placeholder={t("Email")}
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              disabled={isEmailVerified}
                            />
                            <button
                              type="button"
                              className="btn px-3 fw-light btnverify"
                              onClick={handleSendCode}
                              disabled={!email || isEmailVerified}
                            >
                              {t("SendCode")}
                            </button>
                          </div>

                          {emailError && (
                            <small className="text-danger">{emailError}</small>
                          )}

                          {isCodeSent && !isEmailVerified && (
                            <div className="mt-3">
                              <div className="d-flex">
                                <input
                                  type="text"
                                  className="form-control fw-light p-3 me-2"
                                  placeholder={t("EnterVerificationCode")}
                                  value={verificationCode}
                                  onChange={(e) =>
                                    setVerificationCode(e.target.value)
                                  }
                                  disabled={isEmailVerified}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-success px-3"
                                  onClick={handleVerifyCode}
                                >
                                  {t("Verify")}
                                </button>
                              </div>
                            </div>
                          )}

                          {isEmailVerified && (
                            <div className="alert alert-success p-2 text-center fw-light mt-2">
                              {t("EmailVerified")}
                            </div>
                          )}
                        </div>
                      ) : (
                        // GUEST: sadece e-posta inputu, doğrulama yok
                        <div className="mb-3">
                          <input
                            type="email"
                            className="form-control fw-light p-3"
                            placeholder={t("Email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          {/* İstersen buraya küçük bir bilgi notu ekleyebilirsin */}
                          {/* <small className="text-muted">{t("NoEmailVerificationForGuest")}</small> */}
                        </div>
                      )}
                    </div>

                    {/* Adres bölümü */}
                    <div>
                      {userId ? (
                        <>
                          <div>
                            <select
                              className="p-3 mb-3 fw-light form-select"
                              id="addressSelect"
                              onChange={handleAddressChange}
                            >
                              <option value=""> {t("SelectAddress")}</option>
                              {addresses.map((address) => (
                                <option key={address.id} value={address.id}>
                                  {address.title}
                                </option>
                              ))}
                            </select>

                            {selectedAddress && (
                              <div className="address-details">
                                <div className="row mt-3">
                                  <div className="col-lg-12">
                                    <div className="mb-3">
                                      <FormPhoneInput
                                        value={phone}
                                        onChange={setPhone}
                                        placeholder={t("Phone")}
                                        initialCountry="tr"
                                        lockCountry={true}
                                        readOnly={true}
                                        inputStyle={{
                                          height: "55px",
                                          width: "100%",
                                          backgroundColor: "#E9ECEF",
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <div className="col-lg-6">
                                    <div className="mb-3">
                                      <input
                                        type="text"
                                        className="form-control fw-light p-3"
                                        placeholder={t("Address1")}
                                        value={address1}
                                        onChange={(e) =>
                                          setAddress1(e.target.value)
                                        }
                                        disabled
                                      />
                                    </div>
                                  </div>
                                  <div className="col-lg-6">
                                    <div className="mb-3">
                                      <input
                                        type="text"
                                        className="form-control fw-light p-3"
                                        placeholder={t("Address2")}
                                        value={address2}
                                        onChange={(e) =>
                                          setAddress2(e.target.value)
                                        }
                                        disabled
                                      />
                                    </div>
                                  </div>

                                  <LocationInputsForCheckout
                                    country={country}
                                    city={city}
                                    state={state}
                                    setCountry={setCountry}
                                    setCity={setCity}
                                    setState={setState}
                                    geonamesUser="murataktas"
                                    t={t}
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Fatura Tipi (kayıtlı kullanıcı) */}
                          <div>
                            <div className="mb-4">
                              <p className="text-normal fw-14 mb-2">
                                {t("SelectInvoiceType")}
                              </p>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="individual"
                                  checked={selectedTypes.individual}
                                  onChange={handleCheckboxChange}
                                />
                                <label className="form-check-label">
                                  {t("Individual")}
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="corporate"
                                  checked={selectedTypes.corporate}
                                  onChange={handleCheckboxChange}
                                />
                                <label className="form-check-label">
                                  {t("Corporate")}
                                </label>
                              </div>
                            </div>

                            {selectedTypes.individual && (
                              <>
                                <h6 className="mb-3 fw-normal">
                                  {t("IndividualInvoice")}
                                </h6>
                                <div className="row mb-4">
                                  <div className="col-lg-6">
                                    <input
                                      name="full_name"
                                      placeholder={t("FullName")}
                                      value={individualData.full_name}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="email"
                                      placeholder={t("Email")}
                                      value={individualData.email}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                      type="email"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <FormPhoneInput
                                      value={individualData.phone}
                                      onChange={(digits) =>
                                        handleIndividualChange({
                                          target: {
                                            name: "phone",
                                            value: digits,
                                          },
                                        })
                                      }
                                      placeholder={t("Phone")}
                                      initialCountry="tr"
                                      lockCountry={false}
                                      readOnly={false}
                                      inputStyle={{
                                        height: "55px",
                                        width: "100%",
                                      }}
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="personal_tax_number"
                                      placeholder={t("PersonalTaxNumber")}
                                      value={individualData.personal_tax_number}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-12">
                                    <input
                                      name="address"
                                      placeholder={t("Address")}
                                      value={individualData.address}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            {selectedTypes.corporate && (
                              <>
                                <h6 className="mb-3 fw-normal">
                                  {t("CorporateInvoice")}
                                </h6>
                                <div className="row mb-4">
                                  <div className="col-lg-6">
                                    <input
                                      name="email"
                                      placeholder={t("Email")}
                                      value={corporateData.email}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                      type="email"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <FormPhoneInput
                                      value={corporateData.phone}
                                      onChange={(digits) =>
                                        handleCorporateChange({
                                          target: {
                                            name: "phone",
                                            value: digits,
                                          },
                                        })
                                      }
                                      placeholder={t("Phone")}
                                      initialCountry="tr"
                                      lockCountry={false}
                                      readOnly={false}
                                      inputStyle={{
                                        height: "55px",
                                        width: "100%",
                                      }}
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="company_name"
                                      placeholder={t("CompanyName")}
                                      value={corporateData.company_name}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="tax_office"
                                      placeholder={t("TaxOffice")}
                                      value={corporateData.tax_office}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="corporate_tax_number"
                                      placeholder={t("CorporateTaxNumber")}
                                      value={corporateData.corporate_tax_number}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="mersis_no"
                                      placeholder={t("MersisNo")}
                                      value={corporateData.mersis_no}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="address"
                                      placeholder={t("Address")}
                                      value={corporateData.address}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <div className="form-check form-check-inline mt-2">
                                      <input
                                        className="form-check-input p-3"
                                        type="checkbox"
                                        id="inlineCheckbox1"
                                        checked={isEInvoice}
                                        onChange={(e) =>
                                          setIsEInvoice(e.target.checked)
                                        }
                                      />
                                      <label className="form-check-label mx-2 fw-light mt-2">
                                        {t("EInvoiceTaxpayer")}
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="mb-3">
                                <FormPhoneInput
                                  value={phone}
                                  onChange={setPhone}
                                  placeholder={t("Phone")}
                                  initialCountry="tr"
                                  lockCountry={false}
                                  readOnly={false}
                                />
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <div className="mb-3">
                                <input
                                  type="text"
                                  className="form-control fw-light  p-3"
                                  placeholder={t("Address1")}
                                  value={address1}
                                  onChange={(e) => setAddress1(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="mb-3">
                                <input
                                  type="text"
                                  className="form-control fw-light  p-3"
                                  placeholder={t("Address2")}
                                  value={address2}
                                  onChange={(e) => setAddress2(e.target.value)}
                                />
                              </div>
                            </div>

                            <LocationInputsForCheckout
                              country={country}
                              city={city}
                              state={state}
                              setCountry={setCountry}
                              setCity={setCity}
                              setState={setState}
                              geonamesUser="murataktas"
                              t={t}
                            />
                          </div>

                          {/* Fatura Tipi (guest) */}
                          <div>
                            <div className="mb-4">
                              <p className="text-normal fw-14 mb-2">
                                {t("SelectInvoiceType")} ({t("Optional")})
                              </p>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="individual"
                                  checked={selectedTypes.individual}
                                  onChange={handleCheckboxChange}
                                />
                                <label className="form-check-label">
                                  {t("Individual")}
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="corporate"
                                  checked={selectedTypes.corporate}
                                  onChange={handleCheckboxChange}
                                />
                                <label className="form-check-label">
                                  {t("Corporate")}
                                </label>
                              </div>
                            </div>

                            {selectedTypes.individual && (
                              <>
                                <h6 className="mb-3 fw-normal">
                                  {t("IndividualInvoice")}
                                </h6>
                                <div className="row mb-4">
                                  <div className="col-lg-6">
                                    <input
                                      name="full_name"
                                      placeholder={t("FullName")}
                                      value={individualData.full_name}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="email"
                                      placeholder={t("Email")}
                                      value={individualData.email}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                      type="email"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <FormPhoneInput
                                      value={individualData.phone}
                                      onChange={(digits) =>
                                        handleIndividualChange({
                                          target: {
                                            name: "phone",
                                            value: digits,
                                          },
                                        })
                                      }
                                      placeholder={t("Phone")}
                                      initialCountry="tr"
                                      lockCountry={false}
                                      readOnly={false}
                                      inputStyle={{
                                        height: "55px",
                                        width: "100%",
                                      }}
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="personal_tax_number"
                                      placeholder={t("PersonalTaxNumber")}
                                      value={individualData.personal_tax_number}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-12">
                                    <input
                                      name="address"
                                      placeholder={t("Address")}
                                      value={individualData.address}
                                      onChange={handleIndividualChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            {selectedTypes.corporate && (
                              <>
                                <h6 className="mb-3 fw-normal">
                                  Corporate Invoice
                                </h6>
                                <div className="row mb-4">
                                  <div className="col-lg-6">
                                    <input
                                      name="email"
                                      placeholder={t("Email")}
                                      value={corporateData.email}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                      type="email"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <FormPhoneInput
                                      value={corporateData.phone}
                                      onChange={(digits) =>
                                        handleCorporateChange({
                                          target: {
                                            name: "phone",
                                            value: digits,
                                          },
                                        })
                                      }
                                      placeholder={t("Phone")}
                                      initialCountry="tr"
                                      lockCountry={false}
                                      readOnly={false}
                                      inputStyle={{
                                        height: "55px",
                                        width: "100%",
                                      }}
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="company_name"
                                      placeholder={t("CompanyName")}
                                      value={corporateData.company_name}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="tax_office"
                                      placeholder={t("TaxOffice")}
                                      value={corporateData.tax_office}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="corporate_tax_number"
                                      placeholder={t("CorporateTaxNumber")}
                                      value={corporateData.corporate_tax_number}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="mersis_no"
                                      placeholder={t("MersisNo")}
                                      value={corporateData.mersis_no}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <input
                                      name="address"
                                      placeholder={t("Address")}
                                      value={corporateData.address}
                                      onChange={handleCorporateChange}
                                      className="form-control mb-3 p-3 fw-light"
                                    />
                                  </div>
                                  <div className="col-lg-6">
                                    <div className="form-check form-check-inline mt-2">
                                      <input
                                        className="form-check-input p-3"
                                        type="checkbox"
                                        id="inlineCheckbox1"
                                        checked={isEInvoice}
                                        onChange={(e) =>
                                          setIsEInvoice(e.target.checked)
                                        }
                                      />
                                      <label className="form-check-label mx-2 fw-light mt-2">
                                        {t("EInvoiceTaxpayer")}
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ÖDEME ALANI */}
                  {/* ÖDEME ALANI */}
                  {currency === "TRY" ? (
                    <>
                      <div className="d-flex justify-content-between">
                        <h5 className="mt-2">
                          <i className="fa-solid fa-2 me-2 text-dark wbbgcolor text-white p-2"></i>
                          {/* {t("CreditCard")} */}
                          {t("PaymentSelected")}
                        </h5>
                      </div>

                      {/* 3D Secure seçeneği */}
                      {/* <div className="form-check my-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="use3ds"
                          checked={use3ds}
                          onChange={(e) => setUse3ds(e.target.checked)}
                        />
                        <label className="form-check-label">
                          <span className="fw-light">
                            Pay securely with 3D Secure
                          </span>
                        </label>
                      </div>

                      <i className="mt-3 wbtextcolor fw-bold">
                        {t("ProductInstallment")}
                      </i>

                      <input
                        type="number"
                        className="form-control fw-light  p-3 mt-3 mb-3"
                        id="creditcardNumber"
                        name="creditcardNumber"
                        placeholder={t("CreditCardNumber")}
                        value={creditCardNumber}
                        onChange={(e) => {
                          let input = e.target.value.replace(/\D/g, "");
                          if (input.length > 19) input = input.slice(0, 19);
                          setCreditCardNumber(input);
                        }}
                      />

                      {installments.length > 0 && (
                        <div className="mt-2">
                          <select
                            className="form-control fw-light  p-3 mb-3"
                            value={selectedInstallment || ""}
                            onChange={(e) =>
                              setSelectedInstallment(parseInt(e.target.value))
                            }
                          >
                            <option value="">{t("InstallmentOptions")}</option>
                            {installments.map((opt) => (
                              <option
                                key={opt.installmentNumber}
                                value={opt.installmentNumber}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <input
                        type="text"
                        className="form-control fw-light  p-3 mb-3"
                        id="creditcardName"
                        name="creditcardName"
                        placeholder={t("CardHolderName")}
                        value={creditCardName}
                        onChange={(e) => setCreditCardName(e.target.value)}
                      />

                      <input
                        type="text"
                        className="form-control fw-light p-3 mb-3"
                        id="expirationdate"
                        name="expirationdate"
                        placeholder={t("ExpirationDate")}
                        value={expirationDate}
                        onChange={(e) => {
                          let input = e.target.value.replace(/\D/g, "");
                          if (input.length > 4) input = input.slice(0, 4);
                          if (input.length > 2)
                            input = input.slice(0, 2) + "/" + input.slice(2);
                          setExpirationDate(input);
                        }}
                      />

                      <input
                        type="number"
                        className="form-control fw-light  p-3 mb-3"
                        id="securitycode"
                        name="securitycode"
                        placeholder={t("SecurityCode")}
                        value={securityCode}
                        onChange={(e) => {
                          let input = e.target.value.replace(/\D/g, "");
                          if (input.length > 3) input = input.slice(0, 3);
                          setSecurityCode(input);
                        }}
                      /> */}

                      {/* YÖNTEM SEÇİMİ: Payment Card ↔ 3D Secure */}
                      <div className="d-flex align-items-center mb-3 mt-3">
                        <div className="form-check form-check-inline pe-4">
                          <input
                            className="form-check-input rounded-1 p-1"
                            type="radio"
                            name="paymentMethod"
                            id="payMethod-card"
                            checked={!use3ds}
                            onChange={() => setUse3ds(false)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="payMethod-card"
                          >
                            <img
                              src={iziycoPayImage}
                              width={50}
                              alt="Payment Card"
                            />
                          </label>
                        </div>

                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input rounded-1 p-1"
                            type="radio"
                            name="paymentMethod"
                            id="payMethod-3ds"
                            checked={use3ds}
                            onChange={() => setUse3ds(true)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="payMethod-3ds"
                          >
                            <img
                              src={iziyco3dsecureImage}
                              width={50}
                              alt="3D Secure"
                            />
                          </label>
                        </div>
                      </div>

                      {/* !!! ESKİ 3D SECURE CHECKBOX BLOKUNU TAMAMEN KALDIRIYORUZ !!! */}

                      {use3ds ? (
                        <div className="alert alert-primary p-3 fw-light">
                          You’ll enter your card details on your bank’s 3D
                          Secure page.
                        </div>
                      ) : (
                        <>
                          <i className="mt-3 wbtextcolor fw-bold">
                            {t("ProductInstallment")}
                          </i>

                          <input
                            type="number"
                            className="form-control fw-light p-3 mt-3 mb-3"
                            id="creditcardNumber"
                            name="creditcardNumber"
                            placeholder={t("CreditCardNumber")}
                            value={creditCardNumber}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 19) input = input.slice(0, 19);
                              setCreditCardNumber(input);
                            }}
                          />

                          {installments.length > 0 && (
                            <div className="mt-2">
                              <select
                                className="form-control fw-light p-3 mb-3"
                                value={selectedInstallment || ""}
                                onChange={(e) =>
                                  setSelectedInstallment(
                                    parseInt(e.target.value)
                                  )
                                }
                              >
                                <option value="">
                                  {t("InstallmentOptions")}
                                </option>
                                {installments.map((opt) => (
                                  <option
                                    key={opt.installmentNumber}
                                    value={opt.installmentNumber}
                                  >
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <input
                            type="text"
                            className="form-control fw-light p-3 mb-3"
                            id="creditcardName"
                            name="creditcardName"
                            placeholder={t("CardHolderName")}
                            value={creditCardName}
                            onChange={(e) => setCreditCardName(e.target.value)}
                          />

                          <input
                            type="text"
                            className="form-control fw-light p-3 mb-3"
                            id="expirationdate"
                            name="expirationdate"
                            placeholder={t("ExpirationDate")}
                            value={expirationDate}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 4) input = input.slice(0, 4);
                              if (input.length > 2)
                                input =
                                  input.slice(0, 2) + "/" + input.slice(2);
                              setExpirationDate(input);
                            }}
                          />

                          <input
                            type="number"
                            className="form-control fw-light p-3 mb-3"
                            id="securitycode"
                            name="securitycode"
                            placeholder={t("SecurityCode")}
                            value={securityCode}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 3) input = input.slice(0, 3);
                              setSecurityCode(input);
                            }}
                          />
                        </>
                      )}

                      <button
                        className="btn bgcolorfooterbottom fw-14 pe-5 px-5 fw-light p-3 w-100 mb-3"
                        type="button"
                        onClick={(e) =>
                          handleSubmitOrder(e, use3ds ? "iyzico3ds" : "iyzico")
                        }
                      >
                        {t("PaymentComplete")}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between">
                        <h5 className="mt-2">
                          <i className="fa-solid fa-2 me-2 text-dark wbbgcolor text-white p-2"></i>
                          {t("CreditCard")}
                        </h5>
                      </div>

                      {/* Dövizde de aynı radio seçimi kullanılsın */}
                      <div className="d-flex align-items-center my-3">
                        <div className="form-check form-check-inline pe-4">
                          <input
                            className="form-check-input rounded-1 p-1"
                            type="radio"
                            name="paymentMethodFX"
                            id="payMethodFX-card"
                            checked={!use3ds}
                            onChange={() => setUse3ds(false)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="payMethodFX-card"
                          >
                            <img
                              src={iziycoPayImage}
                              width={50}
                              alt="Payment Card"
                            />
                          </label>
                        </div>

                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input rounded-1 p-1"
                            type="radio"
                            name="paymentMethodFX"
                            id="payMethodFX-3ds"
                            checked={use3ds}
                            onChange={() => setUse3ds(true)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="payMethodFX-3ds"
                          >
                            <img
                              src={iziyco3dsecureImage}
                              width={50}
                              alt="3D Secure"
                            />
                          </label>
                        </div>
                      </div>

                      {use3ds ? (
                        <div className="alert alert-primary p-3 fw-light">
                          You’ll enter your card details on your bank’s 3D
                          Secure page.
                        </div>
                      ) : (
                        <>
                          <i className="mt-3 wbtextcolor fw-bold">
                            {t("ProductInstallment")}
                          </i>

                          <input
                            type="number"
                            className="form-control fw-light p-3 mt-3 mb-3"
                            id="creditcardNumber"
                            name="creditcardNumber"
                            placeholder={t("CreditCardNumber")}
                            value={creditCardNumber}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 16) input = input.slice(0, 16);
                              setCreditCardNumber(input);
                            }}
                          />

                          {installments.length > 0 && (
                            <div className="mt-2">
                              <select
                                className="form-control fw-light p-3 mb-3"
                                value={selectedInstallment || ""}
                                onChange={(e) =>
                                  setSelectedInstallment(
                                    parseInt(e.target.value)
                                  )
                                }
                              >
                                <option value="">
                                  {t("InstallmentOptions")}
                                </option>
                                {installments.map((opt) => (
                                  <option
                                    key={opt.installmentNumber}
                                    value={opt.installmentNumber}
                                  >
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <input
                            type="text"
                            className="form-control fw-light p-3 mb-3"
                            id="creditcardName"
                            name="creditcardName"
                            placeholder={t("CardHolderName")}
                            value={creditCardName}
                            onChange={(e) => setCreditCardName(e.target.value)}
                          />

                          <input
                            type="text"
                            className="form-control fw-light p-3 mb-3"
                            id="expirationdate"
                            name="expirationdate"
                            placeholder={t("ExpirationDate")}
                            value={expirationDate}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 4) input = input.slice(0, 4);
                              if (input.length > 2)
                                input =
                                  input.slice(0, 2) + "/" + input.slice(2);
                              setExpirationDate(input);
                            }}
                          />

                          <input
                            type="number"
                            className="form-control fw-light p-3 mb-3"
                            id="securitycode"
                            name="securitycode"
                            placeholder={t("SecurityCode")}
                            value={securityCode}
                            onChange={(e) => {
                              let input = e.target.value.replace(/\D/g, "");
                              if (input.length > 3) input = input.slice(0, 3);
                              setSecurityCode(input);
                            }}
                          />
                        </>
                      )}

                      <button
                        className="btn bgcolorfooterbottom fw-14 pe-5 px-5 fw-light p-3 w-100 mb-3"
                        type="button"
                        onClick={(e) =>
                          handleSubmitOrder(e, use3ds ? "iyzico3ds" : "iyzico")
                        }
                      >
                        {t("PaymentComplete")}
                      </button>

                      <button
                        className="btn bgcolorfooterbottom2 text-white w-100 p-3"
                        type="button"
                        onClick={(e) => handleSubmitOrder(e, "paypal")}
                      >
                        {t("PayWithPaypal")}
                      </button>
                    </>
                  )}
                </form>
              </div>
            </div>

            {/* SAĞ SÜTUN */}
            <div className="col-lg-4">
              <div className="order-summary p-4 bg-white rounded">
                <h5 className="mb-4">{t("MyShoppingCart")}</h5>

                {cartItems.map((item, index) => (
                  <div
                    key={index}
                    className="mb-3 d-flex align-items-center"
                    style={{ gap: "1rem" }}
                  >
                    <img
                      src={getCartItemImage(item)}
                      alt={item.name}
                      width={90}
                      className="flex-shrink-0"
                    />
                    <div className="d-flex flex-column">
                      <h6 className="mb-0">{item.name}</h6>
                      <span className="text-muted fs-11">
                        {t("Color")} : {getLocalizedColorName(item, t)}
                        <span className="mx-1">
                          {" "}
                          | {t("Qty")} : {item.quantity}
                        </span>
                      </span>
                      <h6 className="mt-2">
                        {(() => {
                          const unit = unitInCurrency(item, currency);
                          return formatMoney(
                            unit * (item.quantity || 1),
                            currency
                          );
                        })()}
                      </h6>
                    </div>
                  </div>
                ))}

                <hr />

                <div className="checkout-container">
                  {discountInfo && (
                    <>
                      <div className="d-flex justify-content-between mb-3">
                        <h6 className="mb-0 fw-light">
                          {t("Coupon")} <br />
                          <span className="fs-11">
                            <span className="text-uppercase text-danger">
                              {discountInfo.code}
                            </span>{" "}
                            {t("CouponApplied")}{" "}
                            <span className="text-danger">{discountPct}% </span>
                            {t("Discount")}
                          </span>
                        </h6>
                        <h6 className="mb-0 fw-normal mt-2">
                          {formatMoney(discountCurr, currency)}
                        </h6>
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-between fw-bold mb-3 mt-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="InsuranceCheckDefault"
                        checked={insuranceChecked}
                        onChange={(e) => setInsuranceChecked(e.target.checked)}
                      />
                      <label className="form-check-label">
                        <h6 className="fw-light">{t("Insurance")}</h6>
                      </label>
                    </div>
                    <h6 className="fw-normal">
                      {formatMoney(insuranceCurr, currency)}
                    </h6>
                  </div>

                  <div className="d-flex justify-content-between fw-bold">
                    <h5>{t("Total")}</h5>
                    <h5>{formatMoney(totalCurr, currency)}</h5>
                  </div>
                </div>
              </div>

              {/* Kupon */}
              <div className="bg-white py-4 mt-3 p-4">
                <div className="row">
                  <h5 className="mb-0 mb-3"> {t("DiscountCoupon")}</h5>
                  <div className="input-group mb-4">
                    <input
                      type="text"
                      className="form-control p-3 mb-0 fw-light"
                      value={couponCode}
                      placeholder={t("DiscountCoupon")}
                      aria-describedby="button-addon2"
                      onChange={(e) => {
                        let input = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                        if (input.length > 20) input = input.slice(0, 20);
                        setCouponCode(input);
                      }}
                    />
                    <button
                      className="btn bgcolorfooterbottom fw-light"
                      type="button"
                      id="button-addon2"
                      onClick={applyCoupon}
                      disabled={isApplyingCoupon}
                    >
                      {isApplyingCoupon ? t("Applying") + "..." : t("Apply")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Alt Menü */}
              <div className="bg-white py-4 mt-3">
                <CartBottomMenu />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Checkout;
